import { useEffect, useRef, useCallback } from 'react';
import type { DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/driver-tour.css';
import { Button } from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import { createResortDriver, filterAvailableTourSteps, markTourHintSeen } from '../utils/tourUtils';

const ACCENT = '#2C3E2D';

interface UseModalTourOptions {
  steps: DriveStep[];
  tourKey: string;
  /** When false the driver instance is torn down (e.g. modal closed). */
  enabled: boolean;
  onComplete?: () => void;
}

export function useModalTour({ steps, tourKey, enabled, onComplete }: UseModalTourOptions) {
  const driverRef = useRef<ReturnType<typeof createResortDriver> | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled) {
      driverRef.current?.destroy();
      driverRef.current = null;
      return;
    }

    driverRef.current = createResortDriver(steps, () => onCompleteRef.current?.());
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, [enabled, steps, tourKey]);

  const startTour = useCallback(() => {
    if (!enabled) return;
    const available = filterAvailableTourSteps(steps);
    if (available.length === 0) return;

    markTourHintSeen(tourKey);
    driverRef.current?.setSteps(available);
    driverRef.current?.drive();
  }, [steps, tourKey, enabled]);

  return { startTour, hasSteps: steps.length > 0 };
}

interface ModalTourButtonProps {
  onStart: () => void;
  label?: string;
  disabled?: boolean;
}

/** Compact inline control for modal headers — not the fixed page-level tour button. */
export function ModalTourButton({
  onStart,
  label = 'Form guide',
  disabled = false,
}: ModalTourButtonProps) {
  return (
    <Button
      onClick={onStart}
      disabled={disabled}
      size="small"
      startIcon={<ExploreIcon sx={{ fontSize: 15 }} />}
      aria-label={label}
      sx={{
        color: ACCENT,
        borderRadius: 0,
        px: 1.25,
        py: 0.5,
        minWidth: 'auto',

        fontSize: '0.72rem',
        fontWeight: 500,
        letterSpacing: '0.03em',
        textTransform: 'none',
        border: '1px solid #E2DDD8',
        bgcolor: '#FAFAF8',
        whiteSpace: 'nowrap',
        '&:hover': { bgcolor: '#F0EDE8', borderColor: ACCENT },
      }}
    >
      {label}
    </Button>
  );
}
